import React from "react";
import styled, { css } from "styled-components";
import CircleArrows from "../../../assets/svg/icon-circlearrows.svg?react";
import ArrowForward from "../../../assets/svg/arrow-small-forward.svg?react";
import Close from "../../../assets/svg/icon-close-large.svg?react";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";

import { Trans, useTranslation } from "@coworker/locales";
import { shallowEqual } from "../../../helpers/performance";
import {
  describeAreaFilter,
  areaIsWholeStore,
} from "../../InputPopup/UserFiltersRow/filterChoices";
import { ElidedText } from "@coworker/reusable/components/ElidedText";
import { testBuyCloseReasons } from "../TestBuyDetailPage/useTestbuyOptionsMenuTestbuyService";
import { GLOBAL_PASS_THRESHOLD } from "../../../constants/global";
import { COMPLETED_STATUS } from "../constants";
import { describeDivision } from "../../InputPopup/UserFiltersRow/filterChoices";
import { TestBuyTask } from "@coworker/types/lib/testbuy";
import { useTestbuyTasks } from "../CommonComponents/Repositories/useTestbuyService";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMyStore } from "@coworker/app/src/core/auth/useLoggedInUser";
import { TaskData } from "../CommonComponents/Repositories/testbuy.api";

const ItemContainer = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 92px;
  border-bottom: 1px solid var(--grey200);

  &:last-child {
    border-bottom: none;
  }
`;

const LeftIconContainer = styled("div")`
  width: 69px;
  flex-grow: 0;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const TextContainer = styled("div")`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: left;
  font-size: 14px;
`;

const Subtitles = styled("span")`
  color: var(--grey718);
`;

const RightIconContainer = styled("div")`
  width: 50px;
  flex-grow: 0;
`;

const OrangeCircleArrows = styled(CircleArrows)`
  fill: var(--orange);
`;

const NumberIcon = styled("span")`
  font-size: 16px;
  color: ${(p: { isOk: boolean }) =>
    p.isOk ? css`var(--green)` : css`var(--pink)`};
`;

interface TestBuyListItemProps {
  id: string;
  startDate: string;
  numberOfArticles: number;
  numberOfSucceededArticles: number;
  customName?: string | undefined;
  isMine?: boolean;
  isClosed?: boolean;
  reasonForClosing?: string;
  area: string;
  division: string | undefined;
  testbuyTasks: TestBuyTask[];
}

interface SubtitleProps {
  numberOfArticles: number;
  numberOfSucceededArticles: number;
  numberOfTasks: number;
  numberOfCompletedTasks: number;
  isMine: boolean;
  isSuccessful: boolean;
  isClosed: boolean;
  reasonForClosing?: string;
  area: string | React.ReactNode;
}

const translateReasonForClosing = (reason: string): React.ReactNode => {
  const translationMap = new Map(
    testBuyCloseReasons.map((closeReason) => [
      closeReason.type,
      closeReason.name,
    ])
  );
  return translationMap.get(reason) || <>{reason}</>;
};

const SubtitleInner: React.FC<SubtitleProps> = ({
  numberOfArticles,
  numberOfSucceededArticles,
  numberOfTasks,
  numberOfCompletedTasks,
  isMine,
  isClosed,
  reasonForClosing,
  area,
}) => {
  const numberOfFailedArticles = numberOfArticles - numberOfSucceededArticles;
  let element;

  if (isMine) {
    element = (
      <>
        <Trans values={{ count: numberOfArticles, area }}>
          articlesInAreaString
        </Trans>
        <br />
        <Trans>onlyVisibleToYouString</Trans>
      </>
    );
  } else if (isClosed) {
    element = (
      <>
        <Trans>closedFirstUppercaseString</Trans>
        {": "}
        {translateReasonForClosing(reasonForClosing || "")}
        <br />
        {numberOfTasks > 0 && (
          <Trans count={numberOfTasks} values={{ count: numberOfTasks }}>
            tbTasksCreatedString
          </Trans>
        )}
      </>
    );
  } else {
    element = (
      <>
        <Trans
          values={{
            count: numberOfSucceededArticles,
            total: numberOfArticles,
          }}
        >
          someProductsSucceededString
        </Trans>
        <br />
        <>
          {numberOfFailedArticles !== 0 && (
            <Trans
              count={numberOfCompletedTasks}
              values={{ count: numberOfCompletedTasks }}
            >
              tbTasksCompletedString
            </Trans>
          )}
          {numberOfCompletedTasks < numberOfFailedArticles && (
            <>
              {", "}
              <Trans
                count={numberOfFailedArticles - numberOfCompletedTasks}
                values={{
                  count: numberOfFailedArticles - numberOfCompletedTasks,
                }}
              >
                tbTasksPendingString
              </Trans>
            </>
          )}
        </>
      </>
    );
  }

  return <Subtitles>{element}</Subtitles>;
};

const SubtitleMemo = React.memo(SubtitleInner, (prevProps, nextProps) =>
  shallowEqual(prevProps, nextProps)
);

export const TestBuyItem: React.FC<TestBuyListItemProps> = ({
  id,
  isMine,
  isClosed,
  reasonForClosing,
  numberOfArticles,
  numberOfSucceededArticles,
  startDate,
  area,
  customName,
  division,
  testbuyTasks,
}) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const navigate = useFixaNavigate();
  const { t } = useTranslation();
  const { data: tasks, isLoading } = useTestbuyTasks(testbuyTasks);
  const store = useMyStore();
  // Showing date according to store tz.
  const tz =
    store?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const successPercentage = Math.round(
    (numberOfSucceededArticles / numberOfArticles) * 100
  );

  const isSuccessful = successPercentage >= GLOBAL_PASS_THRESHOLD;

  let leftIcon;
  let dataType;
  if (isMine) {
    leftIcon = <OrangeCircleArrows />;
    dataType = "tb-active";
  } else if (isClosed) {
    leftIcon = <Close />;
    dataType = "tb-closed";
  } else {
    leftIcon = (
      <NumberIcon isOk={isSuccessful}>{successPercentage}%</NumberIcon>
    );
    dataType = "tb-completed";
  }

  const divisionName = division && describeDivision(division);

  const getDivionOrWholeStore = (area: string) => {
    return areaIsWholeStore(area)
      ? division
        ? t(`${divisionName}`)
        : t("wholeStoreString")
      : describeAreaFilter([area]);
  };

  return (
    <ItemContainer
      data-testid={`tblistitem-${id}`}
      onClick={() => !isClosed && navigate(`/testbuy/${id}`)}
      data-type={dataType}
    >
      <LeftIconContainer>{leftIcon}</LeftIconContainer>
      <TextContainer>
        <ElidedText>
          <b>
            {startDate ? dayjs(startDate).tz(tz).format("MMMM D, YYYY") : ""}
            {customName ? ` - ${decodeURIComponent(customName)}` : ""}
          </b>
        </ElidedText>
        <SubtitleMemo
          isMine={!!isMine}
          isSuccessful={isSuccessful}
          isClosed={!!isClosed}
          reasonForClosing={reasonForClosing || ""}
          numberOfArticles={numberOfArticles}
          numberOfSucceededArticles={numberOfSucceededArticles}
          numberOfTasks={testbuyTasks?.length || 0}
          numberOfCompletedTasks={
            !isLoading && tasks
              ? tasks?.filter(
                  (task: TaskData) =>
                    task.state === COMPLETED_STATUS || task.state === "CLOSED"
                ).length
              : 0
          }
          area={getDivionOrWholeStore(area)}
        />
      </TextContainer>
      <RightIconContainer>{!isClosed && <ArrowForward />}</RightIconContainer>
    </ItemContainer>
  );
};
